import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "@/lib/api";

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-[#0e0f14] p-5">
      <div className="text-xs text-zinc-500 tracking-wider uppercase">{label}</div>
      <div className={`text-3xl font-semibold mt-2 ${accent || "text-zinc-100"}`}>{value}</div>
    </div>
  );
}

function relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 0) return "just now";
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleString();
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [sessions, setSessions] = useState({ items: [], ws_online_count: 0, active_count: 0 });
  const [kicking, setKicking] = useState({});

  const loadSessions = async () => {
    try {
      const { data } = await api.get("/admin/active-sessions");
      setSessions(data);
    } catch { /* silent */ }
  };

  useEffect(() => {
    (async () => {
      const [{ data: s }, { data: h }] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/history", { params: { limit: 12 } }),
      ]);
      setStats(s);
      setRecent(h.items || []);
    })();
    loadSessions();
    const t = setInterval(loadSessions, 30_000);
    return () => clearInterval(t);
  }, []);

  const forceLogout = async (u) => {
    if (!confirm(`Force-logout ${u.email}? They will be returned to the login screen.`)) return;
    setKicking({ ...kicking, [u.id]: true });
    try {
      await api.post(`/admin/users/${u.id}/force-logout`);
      await loadSessions();
    } catch (ex) {
      alert(formatErr(ex.response?.data?.detail));
    } finally {
      setKicking((k) => ({ ...k, [u.id]: false }));
    }
  };

  return (
    <div className="p-10 max-w-6xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs tracking-widest text-emerald-400/80">OVERVIEW</div>
          <h1 className="text-2xl font-semibold mt-1">Dashboard</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a
            data-testid="download-installer"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/download/installer`}
            className="px-4 py-2 rounded-md bg-emerald-500 hover:bg-emerald-400 text-zinc-900 text-sm font-medium"
          >
            ↓ Browser installer (.exe)
          </a>
          <a
            data-testid="download-installer-zip"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/download/installer-zip`}
            className="px-4 py-2 rounded-md border border-emerald-500/40 hover:border-emerald-400 text-sm text-emerald-300"
            title="If your browser blocks the .exe with 'Virus detected', download this .zip and extract."
          >
            ↓ .exe in .zip
          </a>
          <a
            data-testid="download-portable"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/download/portable`}
            className="px-4 py-2 rounded-md border border-emerald-500/40 hover:border-emerald-400 text-sm text-emerald-300"
          >
            ↓ Portable (.zip)
          </a>
          <a
            data-testid="download-agent"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/download/agent`}
            className="px-4 py-2 rounded-md bg-amber-500 hover:bg-amber-400 text-zinc-900 text-sm font-medium"
          >
            ↓ Endpoint Agent (.zip)
          </a>
          <a
            data-testid="download-source"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/download/source`}
            className="px-4 py-2 rounded-md border border-zinc-700 hover:border-zinc-500 text-sm"
          >
            ↓ Source
          </a>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat label="Total users" value={stats?.total_users ?? "—"} />
        <Stat label="Active users" value={stats?.active_users ?? "—"} accent="text-emerald-300" />
        <Stat label="Vault entries" value={stats?.vault_entries ?? "—"} />
        <Stat label="URL logs" value={stats?.url_logs ?? "—"} />
        <Stat label="Endpoint events" value={stats?.endpoint_events ?? "—"} accent="text-amber-300" />
      </div>

      {/* Active sessions widget (v1.1.6) */}
      <div className="mt-10" data-testid="active-sessions-widget">
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="text-xs tracking-widest text-zinc-500 uppercase">Active sessions</div>
            <div className="text-[11px] text-zinc-500 mt-1">
              Users active in the last 5 minutes · {sessions.ws_online_count} live on chat · refreshes every 30s
            </div>
          </div>
          <button onClick={loadSessions} data-testid="refresh-sessions"
            className="text-xs px-3 py-1 rounded border border-zinc-700 hover:border-zinc-500">Refresh</button>
        </div>
        <div className="rounded-lg border border-zinc-800 bg-[#0e0f14] divide-y divide-zinc-900">
          {sessions.items.length === 0 && (
            <div className="p-5 text-sm text-zinc-500">No active sessions right now.</div>
          )}
          {sessions.items.map((u) => (
            <div key={u.id} className="p-4 flex items-center justify-between text-sm" data-testid={`session-row-${u.id}`}>
              <div className="min-w-0 flex-1 flex items-center gap-3">
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${u.online_via_ws ? "bg-emerald-400 animate-pulse" : "bg-amber-400"}`} title={u.online_via_ws ? "Live (chat WS)" : "Active (recent activity)"}/>
                <div className="min-w-0">
                  <Link to={`/users/${u.id}`} className="text-zinc-100 hover:text-emerald-300 truncate inline-block">
                    {u.email}
                  </Link>
                  {u.role === "admin" && (
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-700/40 text-emerald-300">ADMIN</span>
                  )}
                  {u.auth_provider === "gosnd" && (
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-violet-500/10 border border-violet-700/40 text-violet-300">SSO</span>
                  )}
                  <div className="text-[11px] text-zinc-500 truncate">
                    {u.name || "—"}
                    {u.designation && ` · ${u.designation}`}
                    {u.department && ` · ${u.department}`}
                  </div>
                </div>
              </div>
              <div className="text-right shrink-0 ml-4 flex items-center gap-3">
                <div>
                  <div className="text-xs text-zinc-300">{relTime(u.last_activity_ts)}</div>
                  <div className="text-[11px] text-zinc-500">last activity</div>
                </div>
                <button
                  onClick={() => forceLogout(u)}
                  disabled={!!kicking[u.id]}
                  data-testid={`session-kick-${u.id}`}
                  className="text-xs px-3 py-1.5 rounded border border-amber-700 text-amber-300 hover:bg-amber-900/20 disabled:opacity-40"
                  title="Force-logout this user immediately"
                >
                  {kicking[u.id] ? "Logging out…" : "Force logout"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10">
        <div className="text-xs tracking-widest text-zinc-500 uppercase mb-3">Recent activity</div>
        <div className="rounded-lg border border-zinc-800 bg-[#0e0f14] divide-y divide-zinc-900">
          {recent.length === 0 && <div className="p-5 text-sm text-zinc-500">No activity yet.</div>}
          {recent.map((r) => (
            <div key={r.id} className="p-4 flex items-center justify-between text-sm">
              <div className="min-w-0">
                <div className="text-zinc-200 truncate" data-testid="recent-url">{r.title || r.url}</div>
                <div className="text-xs text-zinc-500 truncate">{r.url}</div>
              </div>
              <div className="text-right ml-4 shrink-0">
                <div className="text-xs text-emerald-300">{r.user_email}</div>
                <div className="text-[11px] text-zinc-500">{new Date(r.ts).toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
